import React from 'react';
import MailTabs, {sortTags} from 'components/inbox/mail-table/MailTabs';
import MailTable from 'components/inbox/mail-table/MailTable';
import isNil from 'lodash/isNil';
import ActionToolbar from 'components/inbox/mail-toolbar/ActionToolbar';
import FilteringToolbar from 'components/inbox/mail-toolbar/FilteringToolbar';
import MailStore from 'stores/inbox/MailStore';
import FolderStore from 'stores/inbox/FolderStore';
import TagStore from 'stores/TagStore';
import connectToStores from 'alt-utils/lib/connectToStores';
import urlParams from 'utils/urlParams';
import MailActions from 'actions/inbox/MailActions';
import TagActions from 'actions/TagActions';
import debounce from 'lodash/debounce';
import getFilters from 'components/inbox/utils/getFilters';
import {browserHistory} from 'react-router';
import './InboxItemList.styl';
import NavActions from 'actions/NavActions';
import {getQuery} from 'utils/getQueryParam';
import {updateInboxFilters} from 'components/inbox/page-content/Inbox.utils';

@connectToStores
export default class InboxItemList extends React.PureComponent {
  constructor(props) {
    super(props);
    this.updateFilters = this.updateFilters.bind(this);
    this.loadMore = this.loadMore.bind(this);
    this.isLoading = false;
    this.debouncedTagFetch = debounce(filters => {
      TagActions.fetchTags(filters);
    }, 300);
    this.debouncedMailFetch = debounce((filters, page) => {
      MailActions.fetchMail(filters, page);
    }, 300);
  }

  static getStores() {
    return [MailStore, FolderStore, TagStore];
  }

  static getPropsFromStores() {
    const {
      allMail,
      mail,
      mailByFilters,
      ungroupedMail,
      wasError,
      reverseSelectedMail,
      areAllSelected,
    } = MailStore.getState();
    return {
      tags: TagStore.getState().tags,
      folders: FolderStore.getState().folders,
      allMail,
      mail,
      mailByFilters,
      ungroupedMail,
      wasError,
      reverseSelectedMail,
      areAllSelected,
    };
  }

  componentDidMount() {
    setTimeout(async () => {
      const {location} = this.props;
      let filters = getFilters(getQuery(location));

      await TagActions.fetchTags(filters);

      if (!this.resultsAreAlreadyLoaded(filters)) {
        MailActions.fetchMail(filters, 0);
      }
    });
  }

  componentDidUpdate(prevProps) {
    const {tags, folders} = this.props;
    const {query} = this.props.location;
    const filters = getFilters(query);
    const prevFilters = getFilters(getQuery(prevProps.location));
    const defaultTagId = tags?.sort(sortTags).get(0).id;
    const activeFolder = folders.find(f => filters.folderId === f.id);

    if (defaultTagId) {
      const staleTag = tags.every(t => t.id !== filters.tagId);

      if (isNil(filters.tagId) && activeFolder?.isInbox()) {
        // if we have no tagId, and we're in inbox then default to All
        setTimeout(() => this.updateFilters({viewAll: true}));
      } else if (staleTag && !filters.viewAll) {
        // if we have a stale tag (tag does not apply to the current folder) then reset it
        // unless viewAll is set
        setTimeout(() => this.updateFilters({tagId: defaultTagId}));
      }
    }

    const filtersUpdated = !filters.searchValue().equals(prevFilters.searchValue());
    if (filtersUpdated) {
      this.debouncedTagFetch(filters);
      // dispatch during dispatch
      setTimeout(() => {
        MailActions.clearMailForFilters(filters.searchValue());
        document.documentElement.scrollTop = 0;
        this.debouncedMailFetch(filters.searchValue(), parseInt(query.page, 10) || 0);
      });
    }
  }

  resultsAreAlreadyLoaded(filters) {
    const {query} = this.props.location;
    const mailResults = this.props.mailByFilters.get(filters);
    return mailResults && mailResults.page >= (parseInt(query.page, 10) || 0);
  }

  updateFilters(filterChanges) {
    const {location} = this.props;
    updateInboxFilters(filterChanges, location);
  }

  async loadMore() {
    // We're not using state for `isLoading` here because this loading flag needs to change instantly, and we don't rely on it for re-renders.
    // Infinite scroller fires off several of these events at once, and we need to make sure only one gets through.
    if (this.isLoading) return;

    const {query} = this.props.location;
    const page = (parseInt(query.page, 10) || 0) + 1;

    try {
      this.isLoading = true;
      await MailActions.fetchMail(getFilters(query), page);
      browserHistory.replace(this.props.location.pathname + urlParams({...query, page}));
    } catch (e) {
      console.error(e);
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    const {tags, reverseSelectedMail, ungroupedMail, areAllSelected, folders} = this.props;
    const {query} = this.props.location;
    const filters = getFilters(query);
    const mailResults = this.props.mailByFilters.get(filters.searchValue());
    const activeFolder = folders.find(f => f.id === filters.folderId);

    return (
      <div className="mail-list">
        <div className="sticky-wrapper">
          {reverseSelectedMail.size > 0 || areAllSelected ? (
            <ActionToolbar
              targets={reverseSelectedMail.map(id => ungroupedMail.get(id))}
              filters={filters}
              showMoveToTrash={!activeFolder.isTrash()}
              areAllSelected={areAllSelected}
              folders={this.props.folders}
              onActionCompleted={() => {
                TagActions.fetchTags(filters);
                // if there are no results after user updates something,
                // do another fetch. This could probably use some tweaking
                if (!MailStore.getState().mailByFilters.get(filters)) {
                  MailActions.fetchMail(filters, parseInt(query.page, 10) || 0);
                }
                NavActions.inboxMentionsCount.defer();
              }}
            />
          ) : (
            <FilteringToolbar filters={filters} onUpdateFilters={this.updateFilters} />
          )}
        </div>
        {tags && tags.size === 0 ? (
          <p>No tags for this folder matching search conditions.</p>
        ) : (
          <div>
            {tags && (
              <MailTabs
                isInbox={activeFolder?.isInbox()}
                tags={tags}
                filters={filters}
                onUpdateFilters={this.updateFilters}
              />
            )}
            <MailTable
              filters={filters}
              folders={this.props.folders}
              mailResults={mailResults}
              mail={this.props.mail}
              loadMore={this.loadMore}
              wasError={this.props.wasError}
              reverseSelectedMail={reverseSelectedMail}
              areAllSelected={areAllSelected}
            />
          </div>
        )}
      </div>
    );
  }
}
